/**
 * Time-zone helpers. The calendars show times in the zone the user picked in
 * calendar settings (stored server-side, default Asia/Kolkata), which need not
 * be the browser's. Everything here is plain Intl — no date library.
 */

export const DEFAULT_TIME_ZONE = "Asia/Kolkata";

export function browserTimeZone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIME_ZONE;
    } catch {
        return DEFAULT_TIME_ZONE;
    }
}

const validity = new Map<string, boolean>();
export function isValidTimeZone(tz: string): boolean {
    let ok = validity.get(tz);
    if (ok === undefined) {
        try {
            // Blank is no zone (Intl rejects it too); checked here so it never reaches a formatter.
            ok = tz.trim() !== "" && Boolean(new Intl.DateTimeFormat("en-US", { timeZone: tz }));
        } catch {
            ok = false;
        }
        validity.set(tz, ok);
    }
    return ok;
}

/**
 * Windows zone ids (what Windows, and so older native clients, report) for the
 * zones people most often have, mapped to IANA. Anything not here falls back to
 * the device's zone — this is a safety net, not a converter; the server
 * normalises on save.
 */
const WINDOWS_ZONES: Record<string, string> = {
    "India Standard Time": "Asia/Kolkata",
    "Eastern Standard Time": "America/New_York",
    "Central Standard Time": "America/Chicago",
    "Mountain Standard Time": "America/Denver",
    "Pacific Standard Time": "America/Los_Angeles",
    "GMT Standard Time": "Europe/London",
    "W. Europe Standard Time": "Europe/Berlin",
    "Romance Standard Time": "Europe/Paris",
    "Arabian Standard Time": "Asia/Dubai",
    "Singapore Standard Time": "Asia/Singapore",
    "China Standard Time": "Asia/Shanghai",
    "Tokyo Standard Time": "Asia/Tokyo",
    "AUS Eastern Standard Time": "Australia/Sydney",
    "UTC": "UTC"
};

/**
 * The zone to show times in, given whatever the server stored: the value if
 * the browser can use it, a known Windows id mapped to IANA, else the device's
 * zone. Every calendar and task time goes through here, so a zone the browser
 * can't read never throws (it once blanked the whole calendar).
 */
export function resolveTimeZone(tz: string | null | undefined): string {
    const known = knownTimeZone(tz);
    if (known) return known;
    const device = browserTimeZone();
    return isValidTimeZone(device) ? device : "UTC";
}

/** The zone if the browser can use it (or it is a known Windows id), else null — for someone else's zone, where "unknown" is the honest answer. */
export function knownTimeZone(tz: string | null | undefined): string | null {
    if (tz && isValidTimeZone(tz)) return tz;
    const mapped = tz ? WINDOWS_ZONES[tz.trim()] : undefined;
    return mapped && isValidTimeZone(mapped) ? mapped : null;
}

const formatters = new Map<string, Intl.DateTimeFormat>();
function partsFormatter(tz: string): Intl.DateTimeFormat {
    let f = formatters.get(tz);
    if (!f) {
        f = new Intl.DateTimeFormat("en-US", {
            timeZone: resolveTimeZone(tz),
            hourCycle: "h23",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit"
        });
        formatters.set(tz, f);
    }
    return f;
}

export interface ZonedParts {
    year: number;
    /** 1–12 */
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
    /** ISO weekday: 1 = Monday … 7 = Sunday. */
    weekday: number;
}

/** The wall-clock fields of an instant in a zone. */
export function zonedParts(instant: Date, tz: string): ZonedParts {
    const out: Record<string, number> = {};
    for (const p of partsFormatter(tz).formatToParts(instant)) {
        if (p.type !== "literal") out[p.type] = Number(p.value);
    }
    const year = out.year ?? 1970;
    const month = out.month ?? 1;
    const day = out.day ?? 1;
    const utcDay = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    return {
        year,
        month,
        day,
        hour: (out.hour ?? 0) % 24,
        minute: out.minute ?? 0,
        second: out.second ?? 0,
        weekday: ((utcDay + 6) % 7) + 1
    };
}

/** Minutes the zone is ahead of UTC at that instant (IST = 330). */
export function tzOffsetMinutes(instant: Date, tz: string): number {
    const p = zonedParts(instant, tz);
    const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    return Math.round((asUtc - Math.floor(instant.getTime() / 1000) * 1000) / 60_000);
}

/** The instant whose wall clock in `tz` reads year-month-day hour:minute. */
export function zonedToInstant(year: number, month: number, day: number, hour: number, minute: number, tz: string): Date {
    const guess = Date.UTC(year, month - 1, day, hour, minute);
    const first = tzOffsetMinutes(new Date(guess), tz);
    let t = guess - first * 60_000;
    const second = tzOffsetMinutes(new Date(t), tz);
    if (second !== first) t = guess - second * 60_000;
    return new Date(t);
}

/**
 * A Date whose *local* fields equal the wall clock of `instant` in `tz` — so
 * grid code written against getHours()/getDate() works in any zone.
 */
export function toZoned(instant: Date, tz: string): Date {
    const p = zonedParts(instant, tz);
    return new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
}

/** Inverse of {@link toZoned}: read a Date's local fields as a wall clock in `tz`. */
export function fromZoned(wall: Date, tz: string): Date {
    return zonedToInstant(wall.getFullYear(), wall.getMonth() + 1, wall.getDate(), wall.getHours(), wall.getMinutes(), tz);
}

/** Midnight (in `tz`) of the day `instant` falls on, `addDays` days later. */
export function zonedDayStart(instant: Date, tz: string, addDays = 0): Date {
    const p = zonedParts(instant, tz);
    const d = new Date(Date.UTC(p.year, p.month - 1, p.day + addDays));
    return zonedToInstant(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate(), 0, 0, tz);
}

export function formatTime(instant: Date, tz: string): string {
    return instant.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone: resolveTimeZone(tz) });
}

export function formatDay(instant: Date, tz: string): string {
    return instant.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", timeZone: resolveTimeZone(tz) });
}

/** "GMT+5:30" */
export function offsetLabel(tz: string, at: Date = new Date()): string {
    const mins = tzOffsetMinutes(at, tz);
    const sign = mins < 0 ? "-" : "+";
    const abs = Math.abs(mins);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return `GMT${sign}${h}${m ? `:${String(m).padStart(2, "0")}` : ""}`;
}

/** "09:00" → 540. Invalid input → null. */
export function parseClock(value: string | null | undefined): number | null {
    const m = /^(\d{1,2}):(\d{2})$/.exec((value ?? "").trim());
    if (!m) return null;
    const h = Number(m[1]);
    const min = Number(m[2]);
    if (h > 24 || min > 59 || (h === 24 && min > 0)) return null;
    return h * 60 + min;
}

export function formatClock(minutes: number): string {
    const clamped = Math.max(0, Math.min(24 * 60, Math.round(minutes)));
    return `${String(Math.floor(clamped / 60)).padStart(2, "0")}:${String(clamped % 60).padStart(2, "0")}`;
}

const FALLBACK_ZONES = [
    "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Asia/Tokyo", "Asia/Shanghai", "Asia/Hong_Kong",
    "Australia/Sydney", "Europe/London", "Europe/Paris", "Europe/Berlin", "Africa/Johannesburg",
    "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Sao_Paulo", "UTC"
];

/** Every IANA zone the browser knows, falling back to a short list on older engines. */
export function timeZoneOptions(): string[] {
    const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
    try {
        const all = supported ? supported("timeZone") : [];
        if (all.length > 0) return all.includes("UTC") ? all : [...all, "UTC"];
    } catch { /* fall through */ }
    return FALLBACK_ZONES;
}
